import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import RangeCalendar from './RangeCalendar';
import { v3Api } from '../hooks/useApi';

const SLUG_RE = /^[a-z0-9_]+$/;

// '2026-03-10' -> 'Mar 10, 2026' (parseISO keeps date-only at local midnight, no tz shift)
const rangeLabel = (o, t) => {
    if (!o.start_date) return '';
    const f = format(parseISO(o.start_date), 'MMM d, yyyy');
    if (!o.end_date) return `${f} — ${t('pick end')}`;
    return `${f} — ${format(parseISO(o.end_date), 'MMM d, yyyy')}`;
};

/**
 * Modal editor for a seasonal event. Posts to v3Api.seasonal.save().
 * Props: { event?, onClose, onSaved }
 *   - event: existing doc to edit; absent => create mode
 */
export default function EventEditor({ event, onClose, onSaved }) {
    const { t } = useTranslation();
    const isEdit = !!event?.event_key;

    const [eventName, setEventName] = useState(event?.event_name || '');
    const [eventKey, setEventKey] = useState(event?.event_key || '');
    const [category, setCategory] = useState(event?.category || 'other');
    const [description, setDescription] = useState(event?.description || '');
    const [occurrences, setOccurrences] = useState(
        Array.isArray(event?.occurrences) && event.occurrences.length
            ? event.occurrences.map(o => ({
                year: o.year || new Date().getFullYear(),
                start_date: (o.start_date || '').slice(0, 10),
                end_date: (o.end_date || '').slice(0, 10),
            }))
            : [{ year: new Date().getFullYear(), start_date: '', end_date: '' }]
    );
    const [categories, setCategories] = useState([]);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState(null);
    const [openCalIdx, setOpenCalIdx] = useState(0);

    useEffect(() => {
        v3Api.seasonal.categories()
            .then(res => setCategories(res?.data || []))
            .catch(() => setCategories(['religious', 'retail', 'national', 'entertainment', 'other']));
    }, []);

    const validate = useCallback(() => {
        const errs = {};
        if (!eventName.trim()) errs.event_name = t('Event name is required');
        if (!eventKey.trim()) errs.event_key = t('Event key is required');
        else if (!SLUG_RE.test(eventKey)) errs.event_key = t('Use lowercase letters, digits, and underscores only');
        if (!category) errs.category = t('Category is required');

        const occErrs = occurrences.map(o => {
            const e = {};
            if (!o.year) e.year = t('Required');
            if (!o.start_date) e.start_date = t('Required');
            if (!o.end_date) e.end_date = t('Required');
            if (o.start_date && o.end_date && new Date(o.end_date) <= new Date(o.start_date)) {
                e.end_date = t('Must be after start date');
            }
            return e;
        });
        if (occErrs.some(e => Object.keys(e).length)) errs.occurrences = occErrs;
        return errs;
    }, [eventName, eventKey, category, occurrences, t]);

    const handleOccChange = (idx, field, value) => {
        setOccurrences(prev => {
            const next = [...prev];
            next[idx] = { ...next[idx], [field]: field === 'year' ? Number(value) : value };
            return next;
        });
    };

    // Calendar range pick -> sync start/end ISO strings + derive year from start
    const handleRangeSelect = (idx, startIso, endIso) => {
        setOccurrences(prev => {
            const next = [...prev];
            next[idx] = {
                ...next[idx],
                start_date: startIso || '',
                end_date: endIso || '',
                year: startIso ? Number(startIso.slice(0, 4)) : next[idx].year,
            };
            return next;
        });
    };

    const addOccurrence = () => {
        setOccurrences(prev => {
            const next = [
                ...prev,
                { year: (prev[prev.length - 1]?.year || new Date().getFullYear()) + 1, start_date: '', end_date: '' },
            ];
            setOpenCalIdx(next.length - 1);
            return next;
        });
    };

    const removeOccurrence = (idx) => {
        setOccurrences(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
        setOpenCalIdx(cur => (cur === idx ? null : cur > idx ? cur - 1 : cur));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length) return;

        setSaving(true);
        setServerError(null);
        try {
            await v3Api.seasonal.save({
                event_key: eventKey,
                event_name: eventName,
                description,
                category,
                occurrences: occurrences.map(o => ({
                    year: Number(o.year),
                    start_date: o.start_date,
                    end_date: o.end_date,
                })),
            });
            onSaved?.();
            onClose?.();
        } catch (err) {
            setServerError(err?.response?.data?.error || err?.message || t('Save failed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="v3-seasonal-modal-backdrop" onClick={onClose}>
            <div className="v3-seasonal-modal" onClick={e => e.stopPropagation()}>
                <h3>{isEdit ? t('Edit "{{name}}"', { name: event.event_name }) : t('Create event')}</h3>

                <form onSubmit={handleSubmit}>
                    <div className="v3-seasonal-form-row">
                        <label>{t('Event name')}</label>
                        <input
                            type="text"
                            value={eventName}
                            onChange={e => setEventName(e.target.value)}
                            placeholder={t('e.g. Eid Al-Fitr')}
                            autoFocus
                        />
                        {errors.event_name && <div className="v3-seasonal-error">{errors.event_name}</div>}
                    </div>

                    <div className="v3-seasonal-form-row">
                        <label>{t('Event key (slug, immutable)')}</label>
                        <input
                            type="text"
                            value={eventKey}
                            onChange={e => setEventKey(e.target.value.toLowerCase())}
                            placeholder={t('e.g. eid_al_fitr')}
                            disabled={isEdit}
                            pattern="^[a-z0-9_]+$"
                        />
                        {errors.event_key && <div className="v3-seasonal-error">{errors.event_key}</div>}
                    </div>

                    <div className="v3-seasonal-form-row">
                        <label>{t('Category')}</label>
                        <select value={category} onChange={e => setCategory(e.target.value)}>
                            {categories.map(c => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                        {errors.category && <div className="v3-seasonal-error">{errors.category}</div>}
                    </div>

                    <div className="v3-seasonal-form-row">
                        <label>{t('Description')}</label>
                        <textarea
                            rows={2}
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder={t('Optional notes about the event')}
                        />
                    </div>

                    <div className="v3-seasonal-form-row">
                        <label>{t('Occurrences')}</label>
                        <div className="v3-seasonal-occ-grid" style={{ fontSize: 11, color: 'var(--v3-text-muted)' }}>
                            <div>{t('Year')}</div><div>{t('Date range')}</div><div></div>
                        </div>
                        {occurrences.map((o, idx) => {
                            const open = openCalIdx === idx;
                            const hasRange = !!o.start_date;
                            return (
                                <div key={idx}>
                                    <div className="v3-seasonal-occ-grid">
                                        <input
                                            type="number"
                                            min={2000}
                                            max={2100}
                                            value={o.year}
                                            onChange={e => handleOccChange(idx, 'year', e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            className={`v3-seasonal-daterange ${open ? 'active' : ''} ${hasRange ? '' : 'placeholder'}`}
                                            onClick={() => setOpenCalIdx(open ? null : idx)}
                                            aria-expanded={open}
                                        >
                                            <span>{rangeLabel(o, t) || t('Select dates…')}</span>
                                            <span className="v3-seasonal-daterange-icon">📅</span>
                                        </button>
                                        <button
                                            type="button"
                                            className="v3-seasonal-icon-btn danger"
                                            onClick={() => removeOccurrence(idx)}
                                            disabled={occurrences.length === 1}
                                            title={t('Remove occurrence')}
                                        >×</button>
                                    </div>
                                    {open && (
                                        <div className="v3-seasonal-calendar">
                                            <RangeCalendar
                                                start={o.start_date}
                                                end={o.end_date}
                                                year={o.year}
                                                onChange={(s, e) => handleRangeSelect(idx, s, e)}
                                            />
                                            <div className="v3-seasonal-calendar-actions">
                                                <span className="v3-seasonal-calendar-hint">
                                                    {o.start_date && !o.end_date
                                                        ? t('Pick the end date')
                                                        : t('Click start, then end date')}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="v3-seasonal-icon-btn"
                                                    onClick={() => setOpenCalIdx(null)}
                                                >{t('Done')}</button>
                                            </div>
                                        </div>
                                    )}
                                    {errors.occurrences?.[idx] && (
                                        <div className="v3-seasonal-error">
                                            {Object.values(errors.occurrences[idx]).join(' · ')}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        <button type="button" className="v3-seasonal-icon-btn" onClick={addOccurrence} style={{ marginTop: 6 }}>
                            {t('+ Add occurrence')}
                        </button>
                    </div>

                    {serverError && <div className="v3-seasonal-error" style={{ marginTop: 8 }}>{serverError}</div>}

                    <div className="v3-seasonal-modal-actions">
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={onClose} disabled={saving}>
                            {t('Cancel')}
                        </button>
                        <button type="submit" className="v3-btn v3-btn-primary" disabled={saving}>
                            {saving ? t('Saving…') : (isEdit ? t('Save changes') : t('Create event'))}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
